import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HiArrowRight, HiExternalLink } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import data from "../../data/index.json";
import images, { getProjectImage } from "../../constants/images";
import { Section, SectionHeader, reveal } from "../../components";

const CATEGORY_LABEL = {
  web: "Web & Commerce",
  ai: "AI & Data",
  enterprise: "Enterprise ERP",
  education: "Education & CSR",
  other: "Partnership",
};

const onImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = images.fallbackImage;
};

const Work = () => {
  const { t } = useTranslation();
  const works = (data?.portfolio || []).filter((w) => !w.venture);
  const [lead, ...rest] = works;

  return (
    <Section id="work" tone="warm">
      <SectionHeader
        index="05"
        label={t("Our Portfolio")}
        title={
          <>
            {t("Impact")} <em>{t("stories")}</em>
          </>
        }
        lede={t(
          "Explore client engagements, enterprise systems, and education initiatives. Our flagship ventures are featured above."
        )}
        aside={
          <span className="label tabular">
            {works.length} {t("engagements")}
          </span>
        }
      />

      {works.length === 0 ? (
        <p className="mt-16 py-16 text-center text-ink-faint border-t border-[color:var(--rule)]">
          {t("No projects available.")}
        </p>
      ) : (
        <>
          {/* ── Lead story ── */}
          {lead && (
            <motion.article
              {...reveal()}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center mt-14 md:mt-20 pt-12 border-t border-[color:var(--rule)]"
            >
              <Link
                to={`/project/${lead.id}`}
                className="lg:col-span-7 block overflow-hidden bg-paper-tint aspect-[16/10]"
              >
                <img
                  src={getProjectImage(lead.src)}
                  alt={t(lead.title)}
                  loading="lazy"
                  className="img-editorial"
                  onError={onImageError}
                />
              </Link>

              <div className="lg:col-span-5">
                <span className="label text-gold-ink">
                  {t("Featured")} — {t(CATEGORY_LABEL[lead.category] || CATEGORY_LABEL.other)}
                </span>
                <h3 className="display mt-4">
                  <Link to={`/project/${lead.id}`} className="hover:text-ink-soft transition-colors">
                    {t(lead.title)}
                  </Link>
                </h3>
                <p className="prose-body mt-5">{t(lead.description)}</p>

                {lead.details?.tech?.length > 0 && (
                  <p className="mt-6 text-xs text-ink-faint">
                    {lead.details.tech.join("  ·  ")}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-6 mt-8">
                  <Link to={`/project/${lead.id}`} className="link-underline text-sm font-medium">
                    {t("Case Study")}
                    <HiArrowRight aria-hidden />
                  </Link>
                  {lead.link && lead.link.trim() !== "" && (
                    <a
                      href={lead.link}
                      target="_blank"
                      rel="noreferrer"
                      className="link-underline text-sm font-medium"
                    >
                      {t("Visit site")}
                      <HiExternalLink aria-hidden />
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          )}

          {/* ── Index ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-14 mt-16 md:mt-24">
            {rest.map((work, index) => {
              const hasLink = work.link && work.link.trim() !== "";

              return (
                <motion.article key={work.id} {...reveal(index)} className="group flex flex-col">
                  <Link
                    to={`/project/${work.id}`}
                    className="block overflow-hidden bg-paper-tint aspect-[4/3] mb-5"
                  >
                    <img
                      src={getProjectImage(work.src)}
                      alt={t(work.title)}
                      loading="lazy"
                      className="img-editorial"
                      onError={onImageError}
                    />
                  </Link>

                  <div className="flex items-baseline gap-3 border-t border-[color:var(--rule)] pt-3">
                    <span className="label tabular text-gold-ink">
                      {String(index + 2).padStart(2, "0")}
                    </span>
                    <span className="label">
                      {t(CATEGORY_LABEL[work.category] || CATEGORY_LABEL.other)}
                    </span>
                  </div>

                  <h3 className="display-3 mt-3">
                    <Link to={`/project/${work.id}`} className="hover:text-ink-soft transition-colors">
                      {t(work.title)}
                    </Link>
                  </h3>

                  <p className="mt-3 text-[0.9375rem] leading-relaxed text-ink-soft line-clamp-3">
                    {t(work.description)}
                  </p>

                  <div className="flex flex-wrap items-center gap-5 mt-5 pt-1">
                    <Link to={`/project/${work.id}`} className="link-underline text-sm font-medium">
                      {t("Case Study")}
                      <HiArrowRight aria-hidden className="text-sm" />
                    </Link>
                    {hasLink && (
                      <a
                        href={work.link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-ink-faint hover:text-ink transition-colors"
                        aria-label={`${t("Visit site")}: ${t(work.title)}`}
                      >
                        <HiExternalLink />
                      </a>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </>
      )}
    </Section>
  );
};

export default Work;
